import React from "react";
import { Box, Text, useComputedColorScheme } from "@mantine/core";
import { ZoneSymbolKind } from "@utils/TestUtils";

export type SimpleTestPoint = {
  xDeg: number;
  yDeg: number;
  kind: ZoneSymbolKind;
};

const RING_STEP_DEG = 10;

export function getRingAxisCrossingsDeg(maxDeg: number): number[] {
  const ringCount = Math.floor(maxDeg / RING_STEP_DEG);
  const ringRadiiDeg = Array.from({ length: ringCount }, (_, i) => (i + 1) * RING_STEP_DEG);
  return [...ringRadiiDeg.map((radius) => -radius).reverse(), 0, ...ringRadiiDeg];
}

interface DegreeRulerProps {
  maxDeg: number;
  halfWidthDeg: number;
  color: string;
}

const DegreeRuler: React.FC<DegreeRulerProps> = ({ maxDeg, halfWidthDeg, color }) => {
  const labelSizeDeg = maxDeg * 0.06;
  const tickLengthDeg = labelSizeDeg * 0.5;
  const labelBaselineDeg = tickLengthDeg + labelSizeDeg;
  const heightDeg = labelBaselineDeg + labelSizeDeg * 0.4;

  return (
    <svg
      viewBox={`${-halfWidthDeg} 0 ${2 * halfWidthDeg} ${heightDeg}`}
      width="100%"
      overflow="visible"
      data-testid="simple-test-chart-ruler"
    >
      <line
        x1={-maxDeg}
        y1={0}
        x2={maxDeg}
        y2={0}
        stroke={color}
        vectorEffect="non-scaling-stroke"
      />
      {getRingAxisCrossingsDeg(maxDeg).map((crossingDeg) => (
        <g key={crossingDeg}>
          <line
            x1={crossingDeg}
            y1={0}
            x2={crossingDeg}
            y2={tickLengthDeg}
            stroke={color}
            vectorEffect="non-scaling-stroke"
          />
          <text
            x={crossingDeg}
            y={labelBaselineDeg}
            fontSize={labelSizeDeg}
            textAnchor="middle"
            fill={color}
          >
            {Math.abs(crossingDeg)}
          </text>
        </g>
      ))}
    </svg>
  );
};

interface SimpleTestChartProps {
  points: SimpleTestPoint[];
  backgroundUrl?: string;
  maxXDeg?: number;
  maxYDeg?: number;
  dotRadiusDeg?: number;
  sideLabel?: string;
}

const SimpleTestChart: React.FC<SimpleTestChartProps> = ({
  points,
  backgroundUrl,
  maxXDeg = 80,
  maxYDeg = 80,
  dotRadiusDeg = 1.5,
  sideLabel,
}) => {
  const colorScheme = useComputedColorScheme("light");
  const dotColor = colorScheme === "dark" ? "white" : "black";
  const padDeg = dotRadiusDeg * 1.5;
  const totalXDeg = maxXDeg + padDeg;
  const totalYDeg = maxYDeg + padDeg;
  const viewBox = `${-totalXDeg} ${-totalYDeg} ${2 * totalXDeg} ${2 * totalYDeg}`;
  const bgInsetPct = (padDeg / totalYDeg) * 50;

  const isDark = colorScheme === "dark";

  return (
    <Box
      style={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 600px) auto",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Text mx={"md"} style={{ gridColumn: 1, gridRow: 1 }}>
        {sideLabel}
      </Text>
      <Box
        style={{
          gridColumn: 2,
          gridRow: 1,
          width: "100%",
          aspectRatio: `${2 * totalXDeg} / ${2 * totalYDeg}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {backgroundUrl && (
          <Box
            style={{
              position: "absolute",
              inset: `${bgInsetPct}%`,
              backgroundImage: `url(${backgroundUrl})`,
              backgroundSize: "cover",
              filter: isDark ? "invert(1)" : "none",
            }}
          />
        )}
        <svg
          viewBox={viewBox}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{
            position: "relative",
            zIndex: 1,
          }}
        >
          <g transform="scale(1,-1)">
            {points.map((p, idx) => {
              const color = dotColor;
              const smallRadius = Math.max(0.25, dotRadiusDeg * 0.25);
              if (p.kind === null) {
                return <circle key={idx} cx={p.xDeg} cy={p.yDeg} r={smallRadius} fill="gray" />;
              }
              if (p.kind === "relative") {
                const armLength = dotRadiusDeg * 0.8;
                return (
                  <g key={idx} stroke={color} strokeWidth={dotRadiusDeg * 0.35}>
                    <line
                      x1={p.xDeg - armLength}
                      y1={p.yDeg - armLength}
                      x2={p.xDeg + armLength}
                      y2={p.yDeg + armLength}
                    />
                    <line
                      x1={p.xDeg - armLength}
                      y1={p.yDeg + armLength}
                      x2={p.xDeg + armLength}
                      y2={p.yDeg - armLength}
                    />
                  </g>
                );
              }
              if (p.kind === "absolute") {
                return (
                  <rect
                    key={idx}
                    x={p.xDeg - dotRadiusDeg}
                    y={p.yDeg - dotRadiusDeg}
                    width={dotRadiusDeg * 2}
                    height={dotRadiusDeg * 2}
                    fill={color}
                  />
                );
              }
              return (
                <circle
                  key={idx}
                  cx={p.xDeg}
                  cy={p.yDeg}
                  r={dotRadiusDeg}
                  fill="none"
                  stroke={color}
                  strokeWidth={dotRadiusDeg * 0.4}
                />
              );
            })}
          </g>
        </svg>
      </Box>
      <Text mx={"md"} style={{ gridColumn: 3, gridRow: 1 }}>
        {sideLabel}
      </Text>
      <Box style={{ gridColumn: 2, gridRow: 2 }}>
        <DegreeRuler maxDeg={maxXDeg} halfWidthDeg={totalXDeg} color={dotColor} />
      </Box>
    </Box>
  );
};

export default SimpleTestChart;
